import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import squareLogo from '../../public/square.svg';
import { validatePostcode } from '../../services/postcode';
import { submitTreeCollectionRequest } from '../../services/tree-collection';
import countries from '../../utils/countries';
import { formatCurrency } from '../../utils/currency';
import request from '../../utils/request';
import { darkModeCardStyle, tokenize, verifyBuyer } from '../../utils/square';
import Notification from '../Notification';
import Select from '../Select';
import TextInput from '../TextInput';
import Textarea from '../Textarea';
import { areas } from './config';

const COST_PER_TREE = 10;

const TreeCollector: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [availableSlots, setAvailableSlots] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(0);
  const [isOrderConfirmed, setIsOrderConfirmed] = useState<boolean>(false);
  const [isBillingSameAsCollection, setIsBillingSameAsCollection] = useState<boolean>(true);
  const [isSquareInitialized, setIsSquareInitialized] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      billingAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        country: 'GB'
      },
      collectionAddress: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        postcode: ''
      },
      area: '',
      whereWillTheTreeBeLeft: '',
      specialInstructions: '',
      agreement: false
    }
  });

  const [card, setCard] = useState(null);

  let payments = useRef(null);

  useEffect(() => {
    const fetchSlotData = async () => {
      setIsLoading(true);
      const { availableSlots } = (await request('/api/tree-collection/availability')) as {
        availableSlots: number;
      };

      setAvailableSlots(availableSlots);
      setIsLoading(false);
    };

    fetchSlotData();
  }, []);

  useEffect(() => {
    if (quantity) {
      if (!(window as any).Square) {
        throw new Error('Square.js failed to load properly');
      }

      const initializeSquare = async () => {
        payments.current = (window as any).Square.payments(
          process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID,
          process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID
        );

        try {
          setCard(await initializeCard(payments.current));
          setIsSquareInitialized(true);
        } catch (e) {
          console.error('Initializing Card failed', e);
          return;
        }
      };

      if (!isSquareInitialized) {
        initializeSquare();
      }
    }
  }, [isSquareInitialized, quantity]);

  const initializeCard = async (payments: any) => {
    const card = await payments.card({
      style: darkModeCardStyle
    });
    await card.attach('#card-container');
    return card;
  };

  const onQuantityChange = (quantityString: string) => {
    if (quantityString === '-') {
      setQuantity(0);
      setIsSquareInitialized(false);
    } else {
      const qty = Number(quantityString);
      setQuantity(qty);
    }
  };

  const total = quantity * COST_PER_TREE;

  const onSubmit = async (data: any) => {
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      const {
        firstName,
        lastName,
        email,
        phone,
        specialInstructions,
        whereWillTheTreeBeLeft,
        billingAddress,
        collectionAddress,
        area
      } = data;

      const donation = quantity! * COST_PER_TREE;

      const order = {
        firstName,
        lastName,
        email,
        phone,
        specialInstructions,
        whereWillTheTreeBeLeft,
        collectionAddress: Object.keys(collectionAddress)
          .map((key) => collectionAddress[key])
          .filter(Boolean)
          .join(', '),
        donation,
        quantity,
        area
      };

      const paymentAddress = isBillingSameAsCollection ? collectionAddress : billingAddress;

      const token = await tokenize(card);
      const verificationToken = await verifyBuyer(payments.current, token, {
        addressLine1: paymentAddress.addressLine1,
        addressLine2: paymentAddress.addressLine2,
        city: paymentAddress.city,
        county: paymentAddress.county,
        country: paymentAddress.country || 'GB',
        firstName,
        lastName,
        email,
        phone,
        amount: donation
      });

      await submitTreeCollectionRequest({
        payment: {
          token,
          verificationToken
        },
        order
      });

      setIsOrderConfirmed(true);
    } catch (e) {
      setError('We were unable to process your order. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
        <div className="text-white flex flex-col text-center items-center">
          <h4 className="text-xl text-white mb-4">Loading...</h4>
          <svg
            className="animate-spin h-6 w-6 mr-2 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth={4}></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (!availableSlots) {
    return (
      <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
        <div className="text-white flex flex-col text-center items-center">
          <h4 className="text-xl text-white mb-4">
            Sorry, all our Christmas tree collection slots have been booked
          </h4>
          <p className="text-white">
            You can still drop your tree off at Tesco Askham Bar on 10th January 2026 between 9am
            and 4pm.
          </p>
        </div>
      </div>
    );
  }

  if (isOrderConfirmed) {
    return (
      <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
        <div className="text-white flex flex-col text-center items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-20 w-20 opacity-40 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h4 className="text-xl text-rt-orange mb-4">We&apos;ve received your request</h4>
          <p className="text-white">Please check your email for confirmation.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-12 md:gap-8">
      <div className="col-span-12 mb-8 md:mb-0">
        <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
          <div className="text-white flex flex-col text-center items-center">
            <p className="text-white mb-4">How many Christmas Trees would you like collecting?</p>
            <div className="flex items-center">
              <Select
                className="ml-auto w-20"
                label="Tell us how many Christmas Trees you would like collecting"
                hideLabel
                name="number-of-trees"
                id="number-of-trees"
                value={quantity ? quantity.toString() : ''}
                onChange={(evt: { target: { value: string } }) =>
                  onQuantityChange(evt.target.value)
                }>
                <option value="-">-</option>
                {[...new Array(6)].map((_, i) => (
                  <option key={i} value={`${i + 1}`}>
                    {i + 1}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>
      {quantity ? (
        <>
          {error ? (
            <div className="col-span-12">
              <Notification variant="error">{error}</Notification>
            </div>
          ) : null}

          {errors.collectionAddress?.postcode?.type === 'validate' ? (
            <div className="col-span-12">
              <Notification variant="error">
                Unfortunately we are unable to collect from the address you have provided.
              </Notification>
            </div>
          ) : null}
          <div className="col-span-12 md:col-span-7">
            <h3 className="text-xl text-rt-orange mb-4">Contact information</h3>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <TextInput
                type="text"
                id="firstName"
                name="firstName"
                label="First name"
                onChange={(evt) => setValue('firstName', evt.target.value)}
                required
                error={errors.firstName && 'First name is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="lastName"
                name="lastName"
                label="Last name"
                onChange={(evt) => setValue('lastName', evt.target.value)}
                required
                error={errors.lastName && 'Last name is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="email"
                id="email"
                name="email"
                label="Email address"
                onChange={(evt) => setValue('email', evt.target.value)}
                required
                error={errors.email && 'Email address is required'}
                validation={{
                  pattern:
                    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                }}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="tel"
                id="phone"
                name="phone"
                label="Telephone number"
                onChange={(evt) => setValue('phone', evt.target.value)}
                required
                error={errors.phone && 'Telephone number is required'}
                // @ts-ignore
                register={register}
              />
            </div>
            <h3 className="text-xl text-rt-orange mb-4">Collection details</h3>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <TextInput
                type="text"
                id="collectionAddress.addressLine1"
                name="collectionAddress.addressLine1"
                label="Address line 1"
                onChange={(evt) => setValue('collectionAddress.addressLine1', evt.target.value)}
                required
                error={errors?.collectionAddress?.addressLine1 && 'Address line 1 is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="collectionAddress.addressLine2"
                name="collectionAddress.addressLine2"
                label="Address line 2"
                onChange={(evt) => setValue('collectionAddress.addressLine2', evt.target.value)}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="collectionAddress.city"
                name="collectionAddress.city"
                label="City"
                onChange={(evt) => setValue('collectionAddress.city', evt.target.value)}
                required
                error={errors?.collectionAddress?.city && 'City is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="collectionAddress.county"
                name="collectionAddress.county"
                label="County/Region"
                onChange={(evt) => setValue('collectionAddress.county', evt.target.value)}
                required
                error={errors?.collectionAddress?.county && 'County/Region is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="collectionAddress.postcode"
                name="collectionAddress.postcode"
                label="Postcode"
                onChange={(evt) => setValue('collectionAddress.postcode', evt.target.value)}
                required
                error={
                  errors?.collectionAddress?.postcode &&
                  (errors?.collectionAddress?.postcode?.type === 'required'
                    ? 'Postcode is required'
                    : "We can't collect from this postcode")
                }
                // @ts-ignore
                register={register}
                validation={{
                  validate: async (value: string) => {
                    const isValid = await validatePostcode(value);
                    console.log({ isValid });
                  }
                }}
              />
              <div className="col-span-2">
                <Select
                  label="Area/suburb"
                  name="area"
                  id="area"
                  error={errors.area && 'Area is required'}
                  onChange={(evt) => setValue('area', evt.target.value)}
                  // @ts-ignore
                  register={register}
                  required>
                  <option>Please select</option>
                  {areas.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="col-span-2">
                <Textarea
                  id="specialInstructions"
                  name="specialInstructions"
                  label="Any special instructions to find/access the property?"
                  onChange={(evt) => setValue('specialInstructions', evt.target.value)}
                  // @ts-ignore
                  register={register}
                />
              </div>
              <div className="col-span-2">
                <Textarea
                  id="whereWillTheTreeBeLeft"
                  name="whereWillTheTreeBeLeft"
                  label={`Where will the ${quantity > 1 ? 'trees' : 'tree'} be left?`}
                  placeholder="e.g. front garden, side of house, behind gate etc"
                  onChange={(evt) => setValue('whereWillTheTreeBeLeft', evt.target.value)}
                  required
                  error={
                    errors.whereWillTheTreeBeLeft && 'Please tell us where the tree will be left'
                  }
                  // @ts-ignore
                  register={register}
                />
              </div>
            </div>
            <h3 className="text-xl text-rt-orange mb-4">Billing details</h3>

            <label className="mb-4 block">
              <input
                type="checkbox"
                defaultChecked={isBillingSameAsCollection}
                onChange={() => setIsBillingSameAsCollection(!isBillingSameAsCollection)}
              />{' '}
              Billing address same as collection address?
            </label>
            <div className="grid grid-cols-2 gap-4">
              {isBillingSameAsCollection ? null : (
                <>
                  <TextInput
                    type="text"
                    id="billingAddress.addressLine1"
                    name="billingAddress.addressLine1"
                    label="Address line 1"
                    onChange={(evt) => setValue('billingAddress.addressLine1', evt.target.value)}
                    required
                    error={errors?.billingAddress?.addressLine1 && 'Address line 1 is required'}
                    // @ts-ignore
                    register={register}
                  />
                  <TextInput
                    type="text"
                    id="billingAddress.addressLine2"
                    name="billingAddress.addressLine2"
                    label="Address line 2"
                    onChange={(evt) => setValue('billingAddress.addressLine2', evt.target.value)}
                    // @ts-ignore
                    register={register}
                  />
                  <TextInput
                    type="text"
                    id="billingAddress.city"
                    name="billingAddress.city"
                    label="City"
                    onChange={(evt) => setValue('billingAddress.city', evt.target.value)}
                    required
                    error={errors?.billingAddress?.city && 'City is required'}
                    // @ts-ignore
                    register={register}
                  />
                  <TextInput
                    type="text"
                    id="billingAddress.county"
                    name="billingAddress.county"
                    label="County/Region"
                    onChange={(evt) => setValue('billingAddress.county', evt.target.value)}
                    required
                    error={errors?.billingAddress?.county && 'County/Region is required'}
                    // @ts-ignore
                    register={register}
                  />
                  <Select
                    id="billingAddress.country"
                    name="billingAddress.country"
                    label="Country"
                    onChange={(evt) => setValue('billingAddress.country', evt.target.value)}
                    required
                    error={errors?.billingAddress?.country && 'Country is required'}
                    // @ts-ignore
                    register={register}>
                    <option></option>
                    {countries.map(({ name, code }) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </>
              )}
              <div className="col-span-2">
                <label className="text-white mb-2 block text-sm">Payment details</label>
                <div id="card-container"></div>
                <div className="mb-5">
                  <label className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      className="mr-2"
                      {...register('agreement', { required: true })}
                    />
                    <span className="leading-none flex-1">
                      I have read and agree to the{' '}
                      <a href="#instructions" className="underline">
                        drop-off/collection instructions
                      </a>{' '}
                      outlined above and York Round Table&apos;s{' '}
                      <Link href="/privacy-policy">
                        <a className="underline">privacy policy</a>
                      </Link>
                    </span>
                  </label>
                  {errors.agreement ? (
                    <span role="alert" className="text-sm text-red-500" id="agreement-error">
                      Please agree to continue
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="sticky top-5">
              <div className="bg-black bg-opacity-20 rounded-xl p-6 mb-5">
                <h3 className="text-xl text-rt-orange mb-4">Order summary</h3>
                <ul className="mb-4">
                  <li className="flex pb-4 text-white text-sm">
                    <span>{quantity} ✕ Christmas Tree Collection</span>
                    <span className="ml-auto">{formatCurrency(total)}</span>
                  </li>
                  <li className="flex pt-4 text-white text-sm border-t border-white">
                    <span>Total</span>
                    <span className="ml-auto">{formatCurrency(total)}</span>
                  </li>
                </ul>
                <button
                  type="submit"
                  id="card-button"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center bg-rt-orange w-full rounded-xl p-3 text-xl text-center text-white">
                  {isSubmitting ? (
                    <svg
                      className="animate-spin h-6 w-6 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth={4}></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  )}
                  Pay now
                </button>
              </div>
              <div className="flex flex-col items-center opacity-40">
                <span className="text-white text-xs block mb-2">Payment secured by</span>
                <Image src={squareLogo} alt="Square" height={25} width={150} layout="fixed" />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </form>
  );
};

export default TreeCollector;
